/**
 * 見開き・単ページのレイアウト計算（エンジン共通層・FR-FLIP-04〜06）。
 *
 * 綴じは「先のページが recto（表）」という物理に従う。LTR（左綴じ）は先のページが右、
 * RTL（右綴じ）はその左右鏡像になる。1 ページ目は単独（背表紙の裏に対向ページが無い）。
 */
import type { PageInfo } from '../core/manifest';
import type { ReadingDirection, ViewMode } from '../core/options';

/** 見開き 1 枚の内容。null は対向ページなし（表紙裏・巻末）。 */
export interface Spread {
  index: number;
  left: number | null;
  right: number | null;
}

/** 見開きモードで、これ未満の横幅では 2 ページが読めないため単ページにする。 */
const SPREAD_MIN_WIDTH = 600;

/** 見開き総数（0 始まりの index が取り得る数）。 */
export function spreadCount(pageCount: number): number {
  return pageCount <= 1 ? 1 : 1 + Math.ceil((pageCount - 1) / 2);
}

/** ページ番号 → 見開き index。 */
export function spreadOf(pageNo: number): number {
  return pageNo <= 1 ? 0 : 1 + Math.floor((pageNo - 2) / 2);
}

/** 見開き index → 表示するページ（読み方向を反映した左右）。 */
export function spreadAt(
  index: number,
  pageCount: number,
  direction: ReadingDirection,
): Spread {
  const exists = (no: number): number | null => (no >= 1 && no <= pageCount ? no : null);
  // LTR の並び: 先のページが右（recto）。表紙は単独で右。
  const ltrLeft = index === 0 ? null : exists(index * 2);
  const ltrRight = index === 0 ? exists(1) : exists(index * 2 + 1);
  return direction === 'rtl'
    ? { index, left: ltrRight, right: ltrLeft }
    : { index, left: ltrLeft, right: ltrRight };
}

/** auto を実際の表示モードへ解決する。 */
export function resolveMode(
  mode: ViewMode,
  container: { width: number; height: number },
  pageCount: number,
): 'single' | 'spread' {
  if (mode !== 'auto') return mode;
  if (pageCount <= 1) return 'single';
  const landscape = container.width >= container.height;
  return landscape && container.width >= SPREAD_MIN_WIDTH ? 'spread' : 'single';
}

/** 見開きに含まれるページ（null を除く）。 */
export function pagesOf(spread: Spread): number[] {
  return [spread.left, spread.right].filter((no): no is number => no !== null);
}

/**
 * 対向ページが無い見開き（表紙・裏表紙）を中央に寄せるための水平方向のずれ。
 *
 * ページの大きさは見開き時と変えない（本の枠は 2 ページ分のまま）。1 枚しか無いときは、
 * その 1 枚が画面中央に来るように本ごとずらす — 実機の dflip と同じ見え方になる。
 * @param pageWidth 1 ページ分の幅
 */
export function spreadShift(spread: Spread, pageWidth: number): number {
  const hasLeft = spread.left !== null;
  const hasRight = spread.right !== null;
  if (hasLeft === hasRight) return 0; // 両方ある（通常の見開き）／両方無い
  return hasRight ? -pageWidth / 2 : pageWidth / 2;
}

/** めくり 1 回の内訳（エンジン非依存 — css2d と webgl3d が同じ物理を共有する）。 */
export interface FlipPlan {
  /** 動くシートが立っている側（true = 右半分）。単ページ表示では綴じ側で決まる。 */
  fromRight: boolean;
  /** シートの表（角度 0 で見える面）。 */
  front: number | null;
  /** シートの裏（角度 π で見える面）。 */
  back: number | null;
  /** シートの下に見えるページ。 */
  under: { left: number | null; right: number | null };
  /**
   * めくりを逆再生するか（角度が π → 0 へ進む）。
   *
   * 単ページ表示で戻るときに使う。綴じは常に同じ側にあるので、戻るめくりは
   * 「反対側から倒す」のではなく「倒れていた紙が起き上がって戻る」動きになる。
   */
  reverse: boolean;
}

/**
 * めくりの内訳を決める。
 * 去るシートは「めくられる側」に立ち、裏面（着地ページ）を見せながら反対側へ倒れる。
 * 側の決定: LTR は前進で右・後退で左、RTL はその鏡像（背表紙の位置が逆のため）。
 */
export function flipPlan(
  from: Spread,
  target: Spread,
  forward: boolean,
  direction: ReadingDirection,
): FlipPlan {
  const fromRight = forward !== (direction === 'rtl');
  return {
    fromRight,
    front: fromRight ? from.right : from.left,
    back: fromRight ? target.left : target.right,
    under: {
      left: fromRight ? from.left : target.left,
      right: fromRight ? target.right : from.right,
    },
    // 見開きでは綴じが本の中央にあり、進む・戻るのどちらも「綴じから離れる側の紙が
    // 中央へ倒れる」動きになる。逆再生は要らない。
    reverse: false,
  };
}

/**
 * 単ページ表示のめくり（スマートフォンなど、見開きにならない画面）。
 *
 * 綴じはページ自身の端にあり、**進む・戻るのどちらでも同じ側**にある
 * （左綴じなら常に左端）。進行方向で綴じを入れ替えると、戻るときだけ右綴じの
 * 動きになってしまう（iOS 実機で指摘・2026-07-14）。
 *
 * 進む: 表（出発ページ）を見せている紙が綴じのまわりに倒れ、下から着地ページが現れる。
 * 戻る: その逆再生 — 倒れていた紙が起き上がり、表（着地ページ）を見せて着地する。
 */
export function singleFlipPlan(
  from: number,
  to: number,
  forward: boolean,
  direction: ReadingDirection,
): FlipPlan {
  // 綴じ側は読み方向だけで決まる（左綴じ = 紙は綴じの右側に広がる）
  const fromRight = direction !== 'rtl';
  return {
    fromRight,
    front: forward ? from : to, // 角度 0（起きている）で見える面
    back: forward ? to : from, // 角度 π（倒れている）で見える面
    under: { left: forward ? to : from, right: null }, // 単ページ表示は 1 面だけを使う
    reverse: !forward,
  };
}

/** ページ寸法（pt）を得る。無ければ先頭ページで代用する。 */
export function pageSize(pages: readonly PageInfo[], pageNo: number | null): {
  width: number;
  height: number;
} {
  const info = (pageNo !== null ? pages[pageNo - 1] : undefined) ?? pages[0];
  return info ? { width: info.width, height: info.height } : { width: 1, height: 1.414 };
}

/** ホスト（コンテナ）座標系での矩形（CSS ピクセル・左上原点）。 */
export interface ScreenRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** ページが本の中で占める横位置（'single' は中央・'left'/'right' は見開きの左右）。 */
export type PageSlot = 'single' | 'left' | 'right';

/** 指定ページが現在どの位置に表示されているか（表示外は null）。 */
export function pageSlot(
  pageNo: number,
  mode: 'single' | 'spread',
  current: Spread,
  currentPage: number,
): PageSlot | null {
  if (mode === 'single') return pageNo === currentPage ? 'single' : null;
  if (pageNo === current.left) return 'left';
  if (pageNo === current.right) return 'right';
  return null;
}

/**
 * 静止時（めくり中でない）に、あるページがホスト上に占める矩形を返す。
 *
 * 両エンジンは本を「ホスト中央に置き、transform=translate(pan+shift) scale(zoom) を
 * 中心基準で適用する」という同一の幾何で配置する（css2d は CSS transform・webgl3d は
 * 中央に置いた本の scale/position・1:1 の焦点面）。単ページは両者とも中央（x=0）。
 * よってこの純関数を検索ハイライトの唯一の座標源にできる。
 */
export function pageScreenRect(
  container: { width: number; height: number },
  box: { width: number; height: number },
  slot: PageSlot,
  shift: number,
  zoom: number,
  pan: { x: number; y: number },
): ScreenRect {
  const centerXLocal = slot === 'single' ? 0 : slot === 'right' ? box.width / 2 : -box.width / 2;
  const width = box.width * zoom;
  const height = box.height * zoom;
  const cx = container.width / 2 + centerXLocal * zoom + pan.x + shift;
  const cy = container.height / 2 + pan.y;
  return { x: cx - width / 2, y: cy - height / 2, width, height };
}

/** 見開き（または単ページ）をコンテナに収めたときの 1 ページ分の CSS ピクセル寸法。 */
export function fitPageBox(
  container: { width: number; height: number },
  page: { width: number; height: number },
  halves: 1 | 2,
  margin = 0.96,
): { width: number; height: number } {
  const available = {
    width: (container.width * margin) / halves,
    height: container.height * margin,
  };
  const scale = Math.min(available.width / page.width, available.height / page.height);
  return { width: page.width * scale, height: page.height * scale };
}
